<template>
  <div class="menu-select-wrapper" :class="{ 'w-full block': !truncateText, 'w-auto': truncateText }" ref="selectWrapper">
    <slot name="prefix" />

    <ContextMenuWrapper placement="bottom-start" :teleport="teleportMenu">
      <!-- Trigger Button styled like ZtButtonContext -->
      <button
        type="button"
        class="menu-select-trigger"
        :class="{ 'w-full': !truncateText }"
        :aria-haspopup="true"
        :data-testid="dataTestid"
      >
        <span v-if="preLabel" class="text-black dark:text-zinc-300 truncate">{{ preLabel }}</span>
        <span class="text-blue-500 flex-1 text-left" :class="{ truncate: truncateText }" :style="truncateText ? { maxWidth: maxWidth } : {}">{{ truncatedSelectedLabel }}</span>
        <Icon name="general.menuArrow" size="base" class="text-blue-500 transition-transform duration-200 ml-2" />
      </button>

      <!-- Menu items -->
      <template #menu>
        <div v-if="options.length === 0" class="menu-select-item text-zinc-400 dark:text-zinc-500 italic">
          No options available
        </div>

        <MenuLink
          v-for="option in options"
          :key="getOptionValue(option)"
          :title="getOptionLabel(option)"
          :class="{
            'menu-select-item--selected': isSelected(option),
            'menu-select-item--disabled': isDisabled(option)
          }"
          @click="!isDisabled(option) && selectOption(option)"
        >
          {{ getOptionLabel(option) }}
        </MenuLink>
      </template>
    </ContextMenuWrapper>

    <slot name="suffix" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { FormKitFrameworkContext } from '@formkit/core';
import { Icon } from '../../icon';
import { ContextMenuWrapper, MenuLink } from '../../menu';

interface Props {
  context: FormKitFrameworkContext;
}

const props = defineProps<Props>();

// Component state
const selectWrapper = ref<HTMLElement | null>(null);

// Props from FormKit context
const dataTestid = computed(() => String(props.context.dataTestid || props.context.attrs['data-testid'] || ''));
const options = computed(() => props.context.options || []);
const placeholder = computed(() => props.context.attrs.placeholder || 'Select an option');
const preLabel = computed(() => props.context.attrs.preLabel || '');
// Inside a scrolling container the menu would be clipped, so callers there render it in document.body.
const teleportMenu = computed(() => Boolean(props.context.attrs.teleportMenu ?? props.context.attrs['teleport-menu']));
const maxWidth = computed(() => props.context.attrs.maxWidth || props.context.attrs['max-width'] || '160px');
const truncateText = computed(() => {
  // Check both camelCase and kebab-case versions
  const truncateProp = props.context.attrs.truncateText ?? props.context.attrs['truncate-text'];
  return truncateProp !== false; // Default true
});

// Get option value (supports both object and primitive options)
const getOptionValue = (option: any) => {
  if (typeof option === 'object' && option !== null) {
    return option.value !== undefined ? option.value : option.key;
  }
  return option;
};

// Get option label (supports both object and primitive options)
const getOptionLabel = (option: any) => {
  if (typeof option === 'object' && option !== null) {
    return option.label !== undefined ? option.label : option.text || option.value;
  }
  return String(option);
};

// Check if option is selected
const isSelected = (option: any) => {
  const value = getOptionValue(option);
  return value === props.context._value;
};

// Check if option is disabled
const isDisabled = (option: any) => {
  if (typeof option === 'object' && option !== null) {
    return option.disabled === true;
  }
  return false;
};

// Get selected option label
const selectedLabel = computed(() => {
  const currentValue = props.context._value;

  // Find the selected option first
  const selectedOption = options.value.find((option: any) =>
    getOptionValue(option) === currentValue
  );

  // If found, return its label (this handles null values properly)
  if (selectedOption) {
    return getOptionLabel(selectedOption);
  }

  // Only show placeholder if no option is selected and value is truly empty
  if (currentValue === undefined || currentValue === '') {
    return placeholder.value;
  }

  // Fallback to placeholder if value doesn't match any option
  return placeholder.value;
});

// Function to calculate max text length based on container width
const calculateMaxTextLength = (text: string, maxWidthPx: string): string => {
  if (!truncateText.value) return text;

  // Extract numeric value from CSS width (e.g., "160px" -> 160)
  const widthValue = parseInt(maxWidthPx.replace('px', ''));

  // Approximate character width in pixels (adjust based on font)
  const avgCharWidth = 8; // px per character for typical form font
  const maxChars = Math.floor(widthValue / avgCharWidth);

  if (text.length <= maxChars) {
    return text;
  }

  return text.substring(0, maxChars - 3) + '...';
};

// Truncated selected label for display
const truncatedSelectedLabel = computed(() => {
  return calculateMaxTextLength(selectedLabel.value, maxWidth.value);
});

// Select option
const selectOption = (option: any) => {
  const value = getOptionValue(option);
  props.context.node.input(value);
};
</script>

<style scoped>
.menu-select-wrapper {
  @apply relative inline-block;
}

/* ZtButtonContext-style trigger */
.menu-select-trigger {
  @apply flex gap-1 justify-between items-center text-base font-semibold;
  @apply bg-transparent border-none cursor-pointer;
  @apply focus:outline-none;
  @apply p-2 rounded hover:bg-zinc-50 dark:hover:bg-zinc-700;
  min-height: 40px; /* Ensure clickable area */
  min-width: 120px; /* Minimum width for usability */
}

/* Menu item styles for selected/disabled states */
.menu-select-item--selected {
  @apply bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400;
}

.menu-select-item--disabled {
  @apply text-zinc-400 dark:text-zinc-500 cursor-not-allowed;
  pointer-events: none;
}
</style>